import lockApi from "@apis/lock.api";
import { getBrowserTabUuid } from "@utils/app.storage";
import { isDefined } from "@utils/var.check";
import {
  LESSON_LOCK_TAKEN_OVER,
  LOCKABLE_RESOURCE_TYPE,
} from "@plugins/common/service/lock.constant";
import { LessonLockChannelClass } from "@modules/editor/service/lesson.lock.channel";
import { ObjectLockModel } from "@plugins/common/types.ts";

const defaultInterval = 120000; // 2mins

// FIXME: chin teck to extract everything related to lock as plugin / utils. dont put in common.
export default class LockService {
  constructor(
    resourceType,
    resourceUuid,
    failHandler,
    intervalDuration = defaultInterval,
    lessonUuid = null,
  ) {
    this.init(resourceType, resourceUuid, failHandler, intervalDuration, lessonUuid);
  }
  init(resourceType, resourceUuid, failHandler, intervalDuration, lessonUuid) {
    this.resourceType = resourceType;
    this.resourceUuid = resourceUuid;
    this.intervalDuration = intervalDuration;
    this.lessonUuid = lessonUuid;
    this.failHandler = failHandler;
    this.interval = null;
    this.lockIntervalGeneration = 0;
  }
  stopLockInterval() {
    clearInterval(this.interval);
    this.interval = null;
    this.lockIntervalGeneration += 1;
  }
  getBrowserTabUuidIfApplicable() {
    return this.resourceType === LOCKABLE_RESOURCE_TYPE.LESSON ||
      this.resourceType === LOCKABLE_RESOURCE_TYPE.LESSON_ACTIVITY
      ? getBrowserTabUuid()
      : null;
  }
  getResourceLockIdentity() {
    return {
      resourceType: this.resourceType,
      resourceUuid: this.resourceUuid,
    };
  }
  notifyLockTakenOver() {
    const lessonLockChannel = new LessonLockChannelClass();
    const browserTabUuid = getBrowserTabUuid();
    lessonLockChannel.postMessage({
      action: LESSON_LOCK_TAKEN_OVER,
      ...this.getResourceLockIdentity(),
      lessonUuid: this.lessonUuid,
      browserTabUuid,
    });
    lessonLockChannel.close();
  }
  lock(overrideExistingLock = false): Promise<ObjectLockModel> {
    //dont call if any value is missing
    if (!isDefined(this.resourceType) || !isDefined(this.resourceUuid)) return Promise.reject();
    const browserTabUuid = this.getBrowserTabUuidIfApplicable();
    return lockApi.lock(this.resourceType, this.resourceUuid, browserTabUuid, overrideExistingLock);
  }
  unlock(): Promise<unknown> {
    if (!isDefined(this.resourceType) || !isDefined(this.resourceUuid)) return Promise.reject();
    const browserTabUuid = this.getBrowserTabUuidIfApplicable();
    return lockApi.unlock(this.resourceType, this.resourceUuid, browserTabUuid);
  }
  lockOnce(overrideExistingLock = false) {
    return new Promise((resolve, reject) => {
      this.lock(overrideExistingLock).then(
        (response) => {
          if (response.missingBrowserTabUuid || !response.lockedByMe) {
            reject(response);
            this.failHandler();
            return;
          }

          if (overrideExistingLock) {
            this.notifyLockTakenOver();
          }
          resolve();
        },
        (error) => {
          reject(error);
          this.failHandler();
        },
      );
    });
  }
  startLockInterval(overrideExistingLock = false, alreadyAcquired = false) {
    this.stopLockInterval();
    const lockIntervalGeneration = this.lockIntervalGeneration;
    const isCurrentLockInterval = () => lockIntervalGeneration === this.lockIntervalGeneration;
    const startInterval = () => {
      if (!isCurrentLockInterval()) return;

      this.interval = setInterval(() => {
        this.lock().then(
          (response) => {
            if (!isCurrentLockInterval()) return;

            if (response.missingBrowserTabUuid || !response.lockedByMe) {
              this.failHandler(response);
              return;
            }
          },
          () => {
            if (!isCurrentLockInterval()) return;

            this.failHandler();
          },
        );
      }, this.intervalDuration);
    };
    return new Promise((resolve, reject) => {
      if (alreadyAcquired) {
        startInterval();
        resolve();
        return;
      }

      this.lock(overrideExistingLock).then(
        (response) => {
          if (!isCurrentLockInterval()) {
            resolve();
            return;
          }

          if (response.missingBrowserTabUuid || !response.lockedByMe) {
            reject("lockRetrievalFail");
            this.failHandler(response);
            return;
          }

          if (overrideExistingLock) {
            this.notifyLockTakenOver();
          }
          startInterval();
          resolve();
        },
        () => {
          if (!isCurrentLockInterval()) {
            resolve();
            return;
          }

          reject("lockRetrievalFail");
          this.failHandler();
        },
      );
    });
  }
  destroy() {
    this.stopLockInterval();
  }
  checkLock(isEditFlow = true) {
    return new Promise((resolve, reject) => {
      const browserTabUuid = getBrowserTabUuid();
      lockApi.checkLockOwner(this.resourceType, this.resourceUuid, browserTabUuid).then(
        (response) => {
          if (response.isLocked) {
            reject();
            this.failHandler(response, isEditFlow);
          } else {
            resolve();
          }
        },
        () => {
          reject();
          this.failHandler();
        },
      );
    });
  }
}
